import React, { useState } from 'react';
import {
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { IBackupArquivo } from '../types';
import { formatarTamanhoBytes, formatarDataHora } from '../utils';
import StatusBadge from '../../ui/StatusBadge';
import DetalhesArquivo from './DetalhesArquivo';

interface TabelaArquivosProps {
  arquivos: IBackupArquivo[];
  total: number;
  pagina: number;
  linhasPorPagina: number;
  onMudarPagina: (novaPagina: number) => void;
  onMudarLinhasPorPagina: (novasLinhas: number) => void;
  onBaixar: (arquivoID: string, nome: string) => void;
  baixandoArquivoID: string | null;
  token: string;
  mostrarTarefa?: boolean;
}

export const TabelaArquivos: React.FC<TabelaArquivosProps> = ({
  arquivos,
  total,
  pagina,
  linhasPorPagina,
  onMudarPagina,
  onMudarLinhasPorPagina,
  onBaixar,
  baixandoArquivoID,
  token,
  mostrarTarefa = false
}) => {
  const theme = useTheme();
  const [snackbarAberto, setSnackbarAberto] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [detalhesArquivoID, setDetalhesArquivoID] = useState<string | null>(null);

  const copiarNomeParaAreaTransferencia = (nome: string) => {
    navigator.clipboard.writeText(nome)
      .then(() => {
        setSnackbarMsg("Nome do arquivo copiado!");
        setSnackbarAberto(true);
      })
      .catch((err) => {
        console.error("Erro ao copiar nome do arquivo", err);
      });
  };

  const handleMudarPagina = (event: unknown, novaPagina: number) => {
    onMudarPagina(novaPagina);
  };

  const handleMudarLinhasPorPagina = (event: React.ChangeEvent<HTMLInputElement>) => {
    onMudarLinhasPorPagina(parseInt(event.target.value, 10));
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          overflowX: 'auto'
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.5)' : 'rgba(0, 0, 0, 0.02)' }}>
            <TableRow>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600, borderColor: theme.palette.divider }}>Arquivo</TableCell>
              {mostrarTarefa && (
                <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600, borderColor: theme.palette.divider }}>Tarefa</TableCell>
              )}
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600, borderColor: theme.palette.divider }}>Data de Criação</TableCell>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600, borderColor: theme.palette.divider }}>Tamanho</TableCell>
              <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600, borderColor: theme.palette.divider }}>Status</TableCell>
              <TableCell align="right" sx={{ color: theme.palette.text.secondary, fontWeight: 600, borderColor: theme.palette.divider, pr: 3 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {arquivos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={mostrarTarefa ? 6 : 5} align="center" sx={{ py: 6, borderColor: theme.palette.divider }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                    Nenhum arquivo encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              arquivos.map((arquivo) => {
                const isBaixando = baixandoArquivoID === arquivo.arquivoID;
                return (
                  <TableRow
                    key={arquivo.arquivoID}
                    sx={{
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell sx={{ borderColor: theme.palette.divider, color: theme.palette.text.primary }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <InsertDriveFileIcon sx={{ color: theme.palette.primary.light, fontSize: '1.25rem' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                          {arquivo.nome}
                        </Typography>
                      </Box>
                    </TableCell>
                    {mostrarTarefa && (
                      <TableCell sx={{ borderColor: theme.palette.divider, color: theme.palette.text.primary }}>
                        {arquivo.tarefa ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {arquivo.tarefa.nome}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                              {arquivo.tarefa.tipo}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                            Avulso
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={{ borderColor: theme.palette.divider, color: theme.palette.text.secondary }}>
                      {formatarDataHora(arquivo.dataCriacao)}
                    </TableCell>
                    <TableCell sx={{ borderColor: theme.palette.divider, color: theme.palette.text.secondary }}>
                      {formatarTamanhoBytes(arquivo.tamanhoBytes)}
                    </TableCell>
                    <TableCell sx={{ borderColor: theme.palette.divider }}>
                      <StatusBadge status={arquivo.status} />
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: theme.palette.divider, pr: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Copiar nome do arquivo">
                          <IconButton
                            size="small"
                            onClick={() => copiarNomeParaAreaTransferencia(arquivo.nome)}
                            sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.primary.main } }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Ver detalhes do arquivo">
                          <IconButton
                            size="small"
                            onClick={() => setDetalhesArquivoID(arquivo.arquivoID)}
                            sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.secondary.main } }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {arquivo.podeBaixar && (
                          <Tooltip title="Baixar backup">
                            <span>
                              <IconButton
                                size="small"
                                disabled={isBaixando}
                                onClick={() => onBaixar(arquivo.arquivoID, arquivo.nome)}
                                sx={{ color: theme.palette.primary.main, '&:hover': { color: theme.palette.primary.dark } }}
                              >
                                {isBaixando ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <CloudDownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={total}
          rowsPerPage={linhasPorPagina}
          page={pagina}
          onPageChange={handleMudarPagina}
          onRowsPerPageChange={handleMudarLinhasPorPagina}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            color: theme.palette.text.secondary,
            borderTop: `1px solid ${theme.palette.divider}`,
            '.MuiTablePagination-actions': {
              color: theme.palette.text.primary
            },
            '.MuiTablePagination-select': {
              color: theme.palette.text.primary
            }
          }}
        />
      </TableContainer>

      {/* Detalhes do Arquivo */}
      <DetalhesArquivo
        arquivoID={detalhesArquivoID}
        open={detalhesArquivoID !== null}
        onClose={() => setDetalhesArquivoID(null)}
        token={token}
      />

      {/* Feedback de Copiar */}
      <Snackbar
        open={snackbarAberto}
        autoHideDuration={3000}
        onClose={() => setSnackbarAberto(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarAberto(false)}
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: theme.palette.success.main,
            color: '#FFFFFF',
            fontWeight: 600,
            borderRadius: '8px'
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TabelaArquivos;
